/**
 * The cancellation policy, as a popup.
 *
 * The tiers used to be written out wherever they were relevant — under the
 * price on the booking screen, in the private-room "good to know" list — which
 * put three lines of small print in front of a member who was trying to book,
 * and left four phrasings of the same rule to keep in step. The detail now
 * lives in one place (cancelPolicyCopy) and is shown here on demand; the
 * places that spelled it out link to it.
 *
 * Stays until it is closed — escape, the backdrop, or "got it" — because it is
 * read, not glanced at.
 */
import { useEffect, useRef } from "react";
// Its own styles, so it can be dropped into a page that doesn't already pull
// the app stylesheet in (the public group-booking page, say) and still look
// like itself. Repeated imports are deduped by the bundler.
import "./styles/app.css";
import { cancelPolicyCopy } from "./bookingLogic";

export const CancelPolicyModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Focus the one way out, so a keyboard or screen-reader member lands on it
  // rather than somewhere behind the panel.
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const { title, intro, tiers, notes } = cancelPolicyCopy();
  return (
    <div
      className="zz-cancel-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="zz-cancel-policy-title"
      // A tap outside is a dismissal; a tap on the card itself is not.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="zz-cancel-modal-card zz-cancel-policy">
        <h3 id="zz-cancel-policy-title">{title}</h3>
        <p>{intro}</p>
        <ul className="zz-cancel-policy-tiers">
          {tiers.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        {notes.map((n) => (
          <p key={n}>{n}</p>
        ))}
        <button
          ref={closeRef}
          className="zz-btn zz-btn--primary"
          onClick={onClose}
        >
          got it
        </button>
      </div>
    </div>
  );
};

export default CancelPolicyModal;
